import type { SDKModule, SDKContext, FormPayload } from '../types';
import { closestFrom } from '../utils/dom';
import { getDevice, hashSimple } from '../utils/session';
import { log } from '../utils/logger';

/**
 * Form Handler Module
 *
 * Listens for pt-form-submit CustomEvent (dispatched by the editor's inline
 * form script). Collects metadata, deduplicates rapid submissions, and POSTs
 * to /api/form. If the form wrapper carries data-form-action, the SDK
 * mirrors the raw form values to that URL after /api/form succeeds.
 *
 * Wrapper contract (set by editor at publish time):
 *   data-pt-component="dynamic-form"   — wrapper marker (required)
 *   data-form-name="{slug}"            — formName written to payload (required)
 *   data-pt-webhook-id="{id}"          — optional webhook routing id
 *   data-form-action="https://..."     — optional mirror URL
 *
 * Dispatch contract:
 *   pt-form-stored   — successful /api/form (preview: emitted without network call)
 *   pt-form-failed   — /api/form non-2xx or network error
 */
export function createFormHandler(): SDKModule {
  let ctx: SDKContext;
  let handler: ((e: Event) => void) | null = null;

  function handleFormSubmit(e: Event): void {
    const detail = (e as CustomEvent).detail;
    if (!detail || typeof detail !== 'object') return;

    const eventTarget = e.target as HTMLElement | null;
    const wrapper = eventTarget
      ? closestFrom(eventTarget, '[data-pt-component="dynamic-form"]')
      : null;
    if (!wrapper) {
      log('warn', 'pt-form-submit fired outside a dynamic-form wrapper; ignoring');
      return;
    }

    const form = wrapper.querySelector('form');
    const formId = form?.getAttribute('data-pt-id') || form?.id || '';
    const formName = wrapper.getAttribute('data-form-name') || '';
    const webhookId = wrapper.getAttribute('data-pt-webhook-id') || '';
    const mirrorUrl = wrapper.getAttribute('data-form-action') || '';

    if (!formName) {
      log('warn', 'dynamic-form wrapper missing data-form-name (formId=' + formId + ')');
    }

    // Dedup: prevent rapid double-click (3s window)
    const dataHash = hashSimple(detail);
    const dedupKey = '__pt_sub_' + formId + '_' + dataHash;
    try {
      const lastSubmitTime = Number(sessionStorage.getItem(dedupKey) || 0);
      const now = Date.now();
      if (lastSubmitTime && (now - lastSubmitTime) < 3000) {
        log('info', 'form dedup: suppressed duplicate submit for ' + formId);
        return;
      }
      sessionStorage.setItem(dedupKey, String(now));
    } catch (err) {
      log('warn', 'form dedup: sessionStorage unavailable', err);
    }

    // Notify form-lifecycle that this form was submitted
    ctx.emit('form:submitted', { formId });

    // Submission count per session
    let submitCount = 1;
    try {
      const countKey = '__pt_sub_count_' + formId;
      submitCount = Number(sessionStorage.getItem(countKey) || 0) + 1;
      sessionStorage.setItem(countKey, String(submitCount));
    } catch (err) {
      log('warn', 'form submitCount: sessionStorage unavailable', err);
    }

    const payload: FormPayload = {
      pageId: ctx.config.pageId,
      profileId: ctx.config.profileId || '',
      formId,
      formName,
      formValues: JSON.stringify(detail),
      meta: {
        webhookId,
        device: getDevice(),
        hostname: location.hostname,
        pathname: location.pathname,
        referrer: document.referrer || '',
        sessionId: ctx.sessionId,
        utm: ctx.utm,
        submittedAt: new Date().toISOString(),
        submitCount,
        dataHash,
      },
    };

    ctx.post('/api/form', payload).then(
      () => {
        try {
          document.dispatchEvent(new CustomEvent('pt-form-stored', {
            detail: { formId, success: true, preview: ctx.preview },
          }));
        } catch (err) {
          log('warn', 'dispatch pt-form-stored failed', err);
        }
        ctx.emit('form:stored', { formId, success: true });

        if (mirrorUrl) {
          if (ctx.preview) {
            try {
              document.dispatchEvent(new CustomEvent('pt-preview-action', {
                detail: { kind: 'mirror', reason: 'preview: skipped mirror POST', payload: { mirrorUrl, detail } },
              }));
            } catch (mirrorErr) {
              log('warn', 'dispatch preview-action mirror failed', mirrorErr);
            }
          } else {
            fetch(mirrorUrl, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(detail),
              keepalive: true,
            }).then(
              (res) => {
                if (!res.ok) log('warn', 'mirror POST returned ' + res.status + ' for ' + mirrorUrl);
              },
              (mirrorErr) => log('warn', 'mirror POST failed', mirrorErr),
            );
          }
        }
      },
      (err) => {
        log('error', '/api/form failed', err);
        try {
          document.dispatchEvent(new CustomEvent('pt-form-failed', {
            detail: { formId, error: String(err) },
          }));
        } catch (innerErr) {
          log('warn', 'dispatch pt-form-failed failed', innerErr);
        }
        ctx.emit('form:failed', { formId, error: String(err) });
      },
    );

    // Event tracking via ptengine (preview-aware via ctx.track)
    ctx.track('form_submit', { form_id: formId, form_name: formName });
  }

  return {
    name: 'form-handler',
    init(context: SDKContext): void {
      ctx = context;
      handler = handleFormSubmit;
      document.addEventListener('pt-form-submit', handler, true);
    },
    destroy(): void {
      if (handler) {
        document.removeEventListener('pt-form-submit', handler, true);
        handler = null;
      }
    },
  };
}
